import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Form, Input, Button, Checkbox, message, Select } from "antd";
import { Helmet } from "react-helmet";
import {
  BellOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  FileSearchOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { Link, useSearchParams } from "react-router-dom";
import { submitVendorApply } from "../../lib/api/vendorApplyApi";
import {
  APP_NAME,
  APP_LOGO,
  VENDOR_PRIORITY_PLACEMENT_MONTHLY_DISPLAY,
} from "../../helpers/constants";
import { VENDOR_TRADE_CATEGORIES } from "../../constants/vendor-trade-categories";
import { device } from "../../lib/breakpoints";

const PANEL = "#0f172a";
const CARD = "#1e293b";
const DEEP = "#020617";
const BORDER = "#334155";
const TEXT = "#f1f5f9";
const MUTED = "#94a3b8";
const ORANGE = "#ff5722";
const ORANGE_HOVER = "#fb923c";

const Page = styled.div`
  min-height: 100vh;
  background: radial-gradient(
      ellipse 120% 80% at 50% -20%,
      rgba(255, 87, 34, 0.08) 0%,
      transparent 55%
    ),
    linear-gradient(165deg, ${DEEP} 0%, ${PANEL} 42%, #111827 100%);
  padding: clamp(0.75rem, 2vw, 1.25rem) 1.25rem 4rem;
`;

const Shell = styled.div`
  max-width: 600px;
  margin: 0 auto;

  @media (min-width: 768px) {
    max-width: 729px;
  }
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1.35rem;
`;

const BrandLogoWrap = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BrandLogo = styled.img`
  height: 48px;
  width: auto;
  max-width: min(168px, 44vw);
  object-fit: contain;
  display: block;
`;

const BrandText = styled.div`
  text-align: left;
`;

const BrandName = styled.div`
  font-size: 1.12rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${TEXT};
`;

const BrandTag = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${ORANGE};
`;

const Card = styled.div`
  position: relative;
  background: ${CARD};
  border-radius: 14px;
  padding: clamp(1.5rem, 4vw, 2.1rem);
  border: 1px solid ${BORDER};
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.04) inset,
    0 24px 48px -16px rgba(0, 0, 0, 0.45);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 1.25rem;
    right: 1.25rem;
    height: 3px;
    border-radius: 0 0 4px 4px;
    background: linear-gradient(
      90deg,
      ${ORANGE},
      #fb923c,
      rgba(255, 87, 34, 0.35)
    );
    opacity: 0.95;
  }
`;

const Headline = styled.h1`
  margin: 0.35rem 0 0.65rem;
  font-weight: 700;
  color: ${TEXT};
  line-height: 1.25;
  font-size: clamp(1.35rem, 4vw, 1.7rem);
  letter-spacing: -0.03em;
  text-align: center;
`;

/** Second line on mobile; inline with leading space on desktop. */
const HeadlineSecondLine = styled.span`
  display: block;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  letter-spacing: inherit;
  color: inherit;

  @media (min-width: 768px) {
    display: inline;

    &::before {
      content: " ";
    }
  }
`;

const Subhead = styled.p`
  margin: 0 auto 1rem;
  max-width: 36rem;
  font-size: 0.94rem;
  line-height: 1.6;
  color: ${MUTED};
  text-align: center;
  text-wrap: balance;

  strong {
    font-size: inherit;
    font-weight: 600;
    color: ${TEXT};
  }
`;

const PriorityInfoLink = styled(Link)`
  color: ${ORANGE_HOVER};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    color: #fdba74;
    text-decoration: underline;
  }
`;

function VettedPriorityLink({ stopPropagation = false }) {
  return (
    <PriorityInfoLink
      to="/vendors/priority"
      target="_blank"
      rel="noopener noreferrer"
      onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
    >
      vetted priority placement
    </PriorityInfoLink>
  );
}

const GOLD = "#d4a853";
const GOLD_LIGHT = "#f5d78e";
const GOLD_MUTED = "#e8c97a";

const PriorityPlacementHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
`;

const PriorityPlacementEyebrow = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${GOLD};
`;

const OptionalPill = styled.span`
  flex-shrink: 0;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${GOLD_MUTED};
  background: rgba(2, 6, 23, 0.35);
  border: 1px solid rgba(212, 168, 83, 0.35);
`;

const ValueList = styled.ul`
  margin: 0 0 1.15rem;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  text-align: left;

  @media ${device.minTablet} {
    margin-left: auto;
    margin-right: auto;
    max-width: 36rem;
    width: fit-content;
  }
`;

const ValueItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  text-align: left;
`;

const ValueIcon = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  margin-top: 0.12rem;
  color: ${ORANGE};
  font-size: 0.8125rem;
  line-height: 1;

  .anticon {
    font-size: 0.8125rem;
  }
`;

const ValueCopy = styled.span`
  display: block;
  flex: 1;
  font-size: 0.8125rem !important;
  line-height: 1.45 !important;
  color: ${MUTED};
  text-align: left;

  strong {
    color: ${TEXT};
    font-weight: 600;
    font-size: inherit !important;
  }
`;

/** Helper copy under Service ZIP codes: cap sentence on its own line from tablet up. */
const ServiceZipFormExtra = styled.div`
  font-size: 12px !important;
  line-height: 1.35 !important;
  font-weight: 400 !important;
  color: ${MUTED} !important;

  &,
  & span {
    font-size: 12px !important;
    line-height: 1.35 !important;
  }

  .zip-extra-cap {
    display: inline;
  }
  @media (min-width: 768px) {
    .zip-extra-cap {
      display: block;
      margin-top: 0.2rem;
    }
  }
`;

const FIELD_FONT_SIZE = "0.9375rem";
const FIELD_LINE_HEIGHT = "1.5";
const FIELD_MIN_HEIGHT = "46px";
/** Shared horizontal inset for premium + consent checkbox rows. */
const CHECKBOX_SECTION_INSET = "1rem";

const StyledForm = styled(Form)`
  .ant-form-item-label > label {
    color: #e2e8f0 !important;
    font-weight: 600;
    font-size: 0.875rem;
  }
  .ant-form-item-label
    > label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::before {
    color: #f87171 !important;
  }
  .ant-form-item-explain-error {
    color: #fca5a5 !important;
  }
  .ant-form-item-extra {
    color: ${MUTED} !important;
    font-size: 0.8rem;
    margin-top: 0.35rem;
  }
  .vendor-service-zip-item .ant-form-item-extra {
    font-size: 12px !important;
    line-height: 1.35 !important;
    font-weight: 400 !important;
  }
  .vendor-service-zip-item .ant-form-item-extra * {
    font-size: 12px !important;
    line-height: 1.35 !important;
  }
  .vendor-service-zip-item .ant-select-arrow {
    display: none !important;
  }
  .ant-form-item-control-input-content {
    text-align: left;
  }
  .ant-checkbox-wrapper {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    text-align: left;
    white-space: normal;
    color: #e2e8f0 !important;
  }
  .ant-checkbox {
    align-self: flex-start;
    margin-top: 0.2rem;
  }
  .ant-checkbox + span {
    text-align: left;
  }
  /* Compact checkbox copy; checkbox aligned with first line of label. */
  .vendor-apply-checkbox-compact.ant-checkbox-wrapper {
    font-size: 0.875rem !important;
    line-height: 1.5 !important;
    color: #e2e8f0 !important;
    align-items: flex-start !important;
  }
  .vendor-apply-checkbox-compact.ant-checkbox-wrapper .ant-checkbox {
    margin-top: 0.2rem !important;
    align-self: flex-start !important;
    transform: none;
  }
  .vendor-apply-checkbox-compact.ant-checkbox-wrapper .ant-checkbox + span {
    padding-top: 0;
    font-size: inherit !important;
    line-height: inherit !important;
  }
  .vendor-apply-checkbox-compact.ant-checkbox-wrapper strong {
    font-size: inherit !important;
    font-weight: 600 !important;
  }

  /* Premium placement upsell - gold card aligned with other form items. */
  .vendor-priority-placement-item {
    position: relative;
    margin-top: 2rem !important;
    margin-bottom: 1.25rem !important;
    padding: 0.9rem ${CHECKBOX_SECTION_INSET} 0.8rem;
    border-radius: 12px;
    border: 1px solid rgba(212, 168, 83, 0.42);
    background: linear-gradient(
      155deg,
      rgba(212, 168, 83, 0.22) 0%,
      rgba(180, 134, 11, 0.12) 38%,
      rgba(15, 23, 42, 0.55) 100%
    );
    box-shadow:
      0 1px 0 rgba(245, 215, 142, 0.14) inset,
      0 10px 28px -14px rgba(0, 0, 0, 0.45);
  }
  .vendor-priority-placement-item::before {
    content: "";
    position: absolute;
    top: 0;
    left: ${CHECKBOX_SECTION_INSET};
    right: ${CHECKBOX_SECTION_INSET};
    height: 2px;
    border-radius: 0 0 3px 3px;
    background: linear-gradient(
      90deg,
      transparent,
      #d4a853,
      #f5d78e,
      transparent
    );
    opacity: 0.95;
  }
  .vendor-priority-placement-item .ant-form-item-label {
    padding-bottom: 0.45rem !important;
  }
  .vendor-priority-placement-item .ant-form-item-label > label {
    width: 100%;
    height: auto;
  }
  .vendor-priority-placement-item .ant-checkbox-wrapper {
    color: #fef9e8 !important;
  }
  .vendor-priority-placement-item .ant-checkbox-inner {
    border-color: rgba(212, 168, 83, 0.65) !important;
    background: rgba(2, 6, 23, 0.35) !important;
  }
  .vendor-priority-placement-item .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #d4a853 !important;
    border-color: #f5d78e !important;
  }
  .vendor-priority-placement-item a {
    color: #f5d78e !important;
    font-weight: 600;
  }
  .vendor-priority-placement-item a:hover {
    color: #fff !important;
  }

  /* Match premium card content inset so both checkboxes share one vertical line. */
  .vendor-network-consent-item {
    padding-inline: ${CHECKBOX_SECTION_INSET} !important;
    margin-bottom: 1.25rem !important;
  }
  /*
   * Ant Design 5 reserves error space by mirroring margin-bottom into
   * .ant-form-item-additional min-height, then cancels it with a negative
   * .ant-form-item-margin-offset sibling. That collapses the gap below multi-line
   * errors - disable the offset and control spacing ourselves.
   */
  .vendor-network-consent-item .ant-form-item-margin-offset {
    display: none !important;
  }
  .vendor-network-consent-item.ant-form-item-has-error {
    margin-bottom: 1.75rem !important;
  }
  .vendor-network-consent-item.ant-form-item-has-error .ant-form-item-additional {
    min-height: auto !important;
    margin-top: 0.35rem;
    margin-bottom: 0.85rem;
  }
  .vendor-network-consent-item .ant-form-item-explain-error {
    margin-top: 0.35rem;
    line-height: 1.45;
  }
  .vendor-network-consent-item.ant-form-item-has-error + .ant-form-item {
    margin-top: 0.25rem !important;
  }

  .ant-input:not(textarea),
  .ant-input-affix-wrapper:not(.ant-input-textarea-affix-wrapper) {
    border-radius: 8px !important;
    background: ${DEEP} !important;
    color: ${TEXT} !important;
    border-color: ${BORDER} !important;
    font-size: ${FIELD_FONT_SIZE} !important;
    line-height: ${FIELD_LINE_HEIGHT} !important;
    height: ${FIELD_MIN_HEIGHT} !important;
    min-height: ${FIELD_MIN_HEIGHT} !important;
    padding-block: 0 !important;
    padding-inline: 0.9rem !important;
    box-sizing: border-box !important;
  }
  .ant-input-affix-wrapper:not(.ant-input-textarea-affix-wrapper)
    > input.ant-input {
    height: 100% !important;
    min-height: unset !important;
    padding: 0 !important;
    line-height: ${FIELD_LINE_HEIGHT} !important;
  }
  textarea.ant-input {
    border-radius: 8px !important;
    background: ${DEEP} !important;
    color: ${TEXT} !important;
    border-color: ${BORDER} !important;
    padding: 0.65rem 0.9rem;
    font-size: ${FIELD_FONT_SIZE} !important;
    line-height: ${FIELD_LINE_HEIGHT} !important;
    min-height: unset !important;
    resize: vertical;
  }
  .vendor-apply-notes-item textarea.ant-input,
  .vendor-apply-notes-item
    .ant-input-textarea-affix-wrapper
    textarea.ant-input {
    min-height: 6.25rem !important;
    padding: 0.65rem 0.9rem 1.65rem !important;
    line-height: ${FIELD_LINE_HEIGHT} !important;
  }
  .vendor-apply-notes-item .ant-input-textarea-affix-wrapper {
    background: ${DEEP} !important;
    border-color: ${BORDER} !important;
    border-radius: 8px !important;
    padding: 0 !important;
    box-shadow: none !important;
  }
  .vendor-apply-notes-item .ant-input-textarea-affix-wrapper:hover {
    border-color: #475569 !important;
  }
  .vendor-apply-notes-item .ant-input-textarea-affix-wrapper:focus-within {
    border-color: rgba(255, 87, 34, 0.55) !important;
    box-shadow: 0 0 0 1px rgba(255, 87, 34, 0.12) !important;
  }
  .vendor-apply-notes-item
    .ant-input-textarea-affix-wrapper
    textarea.ant-input {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
  }
  .vendor-apply-notes-item
    .ant-input-textarea-affix-wrapper
    textarea.ant-input:focus {
    border: none !important;
    box-shadow: none !important;
  }
  .vendor-apply-notes-item .ant-input-data-count,
  .vendor-apply-notes-item .ant-input-textarea-show-count::after {
    color: ${MUTED} !important;
    font-size: 0.75rem !important;
    line-height: 1.2 !important;
  }
  .vendor-apply-notes-item .ant-input-textarea-show-count {
    position: relative;
  }
  .vendor-apply-notes-item .ant-input-textarea-show-count::after {
    inset-inline-end: 0.75rem !important;
    bottom: 0.5rem !important;
    pointer-events: none;
  }
  .ant-input-affix-wrapper > input.ant-input {
    font-size: ${FIELD_FONT_SIZE} !important;
    line-height: ${FIELD_LINE_HEIGHT} !important;
  }
  .ant-input::placeholder,
  textarea.ant-input::placeholder {
    color: #64748b !important;
  }
  .ant-input:hover,
  .ant-input-affix-wrapper:hover,
  textarea.ant-input:hover {
    border-color: #475569 !important;
  }
  .ant-input:focus,
  .ant-input-affix-wrapper-focused {
    border-color: rgba(255, 87, 34, 0.65) !important;
    box-shadow: 0 0 0 2px rgba(255, 87, 34, 0.15) !important;
  }
  textarea.ant-input:focus {
    border-color: rgba(255, 87, 34, 0.55) !important;
    box-shadow: 0 0 0 1px rgba(255, 87, 34, 0.12) !important;
    outline: none !important;
  }
  .ant-checkbox-inner {
    background-color: ${DEEP} !important;
    border-color: ${BORDER} !important;
  }
  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${ORANGE} !important;
    border-color: ${ORANGE} !important;
  }
  .ant-select-multiple,
  .ant-select-multiple.ant-select-show-arrow {
    height: auto !important;
  }
  .ant-select-selector {
    border-radius: 8px !important;
    background: ${DEEP} !important;
    color: ${TEXT} !important;
    border-color: ${BORDER} !important;
    min-height: ${FIELD_MIN_HEIGHT} !important;
    height: ${FIELD_MIN_HEIGHT} !important;
    font-size: ${FIELD_FONT_SIZE} !important;
    line-height: ${FIELD_LINE_HEIGHT} !important;
    padding-block: 0 !important;
    padding-inline: 0.9rem !important;
    display: flex !important;
    align-items: center !important;
    box-sizing: border-box !important;
  }
  .ant-select-multiple .ant-select-selector {
    padding-inline-end: 1.75rem !important;
  }
  .ant-select-multiple .ant-select-selection-wrap,
  .ant-select-multiple .ant-select-selection-overflow {
    align-self: center !important;
  }
  .ant-select-multiple:has(.ant-select-selection-item) .ant-select-selector {
    height: auto !important;
    min-height: ${FIELD_MIN_HEIGHT} !important;
    padding-block: 0.4rem !important;
    align-items: flex-start !important;
  }
  .ant-select-multiple:has(.ant-select-selection-item)
    .ant-select-selection-wrap {
    height: auto !important;
    align-self: flex-start !important;
  }
  .ant-select-selection-overflow {
    align-items: center;
    flex-wrap: wrap !important;
    row-gap: 0.35rem !important;
    column-gap: 0.25rem !important;
    max-width: 100%;
  }
  .ant-select-selection-search-input,
  .ant-select-selection-search-input input,
  .ant-select-selection-placeholder,
  .ant-select-selection-item,
  .ant-select-selection-item-content,
  .ant-select-selection-item-content span,
  .ant-select-selection-overflow-item,
  .ant-select-selection-overflow-item span {
    font-size: ${FIELD_FONT_SIZE} !important;
    line-height: ${FIELD_LINE_HEIGHT} !important;
  }
  .ant-select:hover .ant-select-selector {
    border-color: #475569 !important;
  }
  .ant-select-focused .ant-select-selector,
  .ant-select-open .ant-select-selector {
    background: #ffffff !important;
    border-color: rgba(255, 87, 34, 0.65) !important;
    box-shadow: 0 0 0 2px rgba(255, 87, 34, 0.15) !important;
    color: #0f172a !important;
  }
  .ant-select-focused .ant-select-selection-search-input,
  .ant-select-open .ant-select-selection-search-input {
    color: #0f172a !important;
  }
  .ant-select-focused .ant-select-selection-placeholder,
  .ant-select-open .ant-select-selection-placeholder {
    color: #64748b !important;
  }
  .ant-select-selection-item {
    display: inline-flex !important;
    align-items: center !important;
    gap: 0.2rem;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    line-height: ${FIELD_LINE_HEIGHT} !important;
    font-size: ${FIELD_FONT_SIZE} !important;
    background: rgba(51, 65, 85, 0.85) !important;
    border: 1px solid #475569 !important;
    border-radius: 6px !important;
    color: #ffffff !important;
  }
  .ant-select-selection-item-content {
    color: #ffffff !important;
    line-height: ${FIELD_LINE_HEIGHT} !important;
    font-size: ${FIELD_FONT_SIZE} !important;
    display: inline-flex;
    align-items: center;
  }
  .ant-select-selection-item-remove {
    color: #ffffff !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    line-height: 1 !important;
    margin-inline-start: 0.1rem !important;
  }
  .ant-select-selection-item-remove:hover {
    color: #ffffff !important;
    opacity: 0.95;
  }
  .ant-select-selection-item-remove .anticon {
    color: #ffffff !important;
    vertical-align: 0 !important;
  }
  .vendor-service-zip-item .ant-select-selection-item-remove svg,
  .vendor-service-zip-item .ant-select-selection-item-remove svg path {
    color: #ffffff !important;
    fill: currentColor !important;
  }
  .vendor-service-zip-item .ant-select-selection-item-remove:hover svg,
  .vendor-service-zip-item .ant-select-selection-item-remove:hover svg path {
    color: #ffffff !important;
    fill: currentColor !important;
  }
  .ant-select-selection-placeholder {
    color: #64748b !important;
  }
  .ant-select-arrow {
    color: ${MUTED} !important;
  }
  .vendor-trade-select .ant-select-selection-item-remove svg,
  .vendor-trade-select .ant-select-selection-item-remove svg path {
    color: #ffffff !important;
    fill: currentColor !important;
  }
  .vendor-trade-select .ant-select-selection-item-remove:hover svg,
  .vendor-trade-select .ant-select-selection-item-remove:hover svg path {
    color: #ffffff !important;
    fill: currentColor !important;
  }
`;

const SubmitButton = styled(Button)`
  height: 48px !important;
  border-radius: 8px !important;
  font-weight: 700 !important;
  font-size: 0.95rem !important;
  border: none !important;
  background: ${ORANGE} !important;
  color: #fff !important;
  box-shadow: 0 4px 14px rgba(255, 87, 34, 0.35);
  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    background: ${ORANGE_HOVER} !important;
    color: #fff !important;
    box-shadow: 0 6px 20px rgba(255, 87, 34, 0.4) !important;
  }
  &:disabled,
  &[disabled] {
    background: ${BORDER} !important;
    color: ${MUTED} !important;
    border: 1px solid ${BORDER} !important;
    box-shadow: none !important;
    cursor: not-allowed !important;
    opacity: 0.85;
  }
`;

const FooterNote = styled.p`
  margin: 1.35rem 0 0;
  text-align: center;
  font-size: 0.8rem;
  color: ${MUTED};
  a {
    color: ${ORANGE_HOVER};
    font-weight: 500;
    &:hover {
      color: #fdba74;
    }
  }
`;

const SuccessPanel = styled.div`
  text-align: center;
  padding: 0.5rem 0 0.25rem;
`;

const SuccessTitle = styled.h2`
  margin: 0 0 0.65rem;
  font-size: clamp(1.35rem, 4vw, 1.6rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  color: ${TEXT};
  line-height: 1.25;
`;

const PROGRAM_PRIORITY = "priority_placement";
const VENDOR_VALUE_PROPS = [
  {
    Icon: FileSearchOutlined,
    title: "Scope before you quote",
    text: "Problem, property, ZIP, and timeline when you're invited to bid.",
  },
  {
    Icon: EnvironmentOutlined,
    title: "Only relevant jobs",
    text: "Matched to your trades and service ZIPs, not mass lead lists.",
  },
  {
    Icon: ToolOutlined,
    title: "Real owner projects",
    text: "Repairs and remodels from owners actively lining up pros.",
  },
  {
    Icon: BellOutlined,
    title: "Fast alerts",
    text: "Email or SMS when there's a fit for your crew.",
  },
];

const PROGRAM_NETWORK = "network";

const MAX_SERVICE_ZIPS = 25;

/**
 * From tags mode values, produce unique U.S. ZIPs: digits only, chunked in groups of 5.
 * Tokens with more than 5 digits become multiple zips (e.g. ten digits -> two zips); a trailing run shorter than 5 digits is dropped.
 */
function normalizeZipTagValues(value) {
  if (!value || !Array.isArray(value)) return [];
  const seen = new Set();
  const out = [];
  for (const raw of value) {
    const d = String(raw ?? "").replace(/\D/g, "");
    if (!d) continue;
    for (let i = 0; i + 5 <= d.length; i += 5) {
      const z = d.slice(i, i + 5);
      if (!seen.has(z)) {
        seen.add(z);
        out.push(z);
      }
    }
  }
  return out.slice(0, MAX_SERVICE_ZIPS);
}

/** Normalize form value (tags) to unique 5-digit ZIP strings. */
function serviceZipsFromFormValue(value) {
  return normalizeZipTagValues(value);
}

/** Strip to digits; valid if exactly 10 US digits (optional leading 1). */
function normalizeUsPhoneDigits(raw) {
  const digits = String(raw ?? "").replace(/\D/g, "");
  if (digits.length === 10) return digits;
  if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1);
  return null;
}

/** Format as user types; caps at 10 digits (ignores extra pasted digits). */
function formatUsPhoneInput(raw) {
  let digits = String(raw ?? "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  digits = digits.slice(0, 10);

  if (!digits) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function isValidEmail(value) {
  const v = String(value || "").trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

/** True when every required field and checkbox is satisfied. */
function isVendorApplyFormReady(values) {
  if (!values || typeof values !== "object") return false;

  const name = String(values.name || "").trim();
  if (name.length < 2) return false;

  const trade = values.trade;
  const hasTrade = Array.isArray(trade)
    ? trade.length > 0
    : String(trade || "").trim().length >= 2;
  if (!hasTrade) return false;

  if (serviceZipsFromFormValue(values.zipCode).length === 0) return false;
  if (!normalizeUsPhoneDigits(values.phone)) return false;
  if (!isValidEmail(values.email)) return false;
  if (values.clumsePreferredVendorInterest !== true) return false;

  return true;
}

/**
 * Custom onChange that limits tags to 5-digit ZIPs and caps the search input to 5 digits.
 */
function VendorServiceZipSelect(props) {
  const { value, onChange, onBlur, ...rest } = props;
  const [searchValue, setSearchValue] = useState("");

  const handleSearch = (val) => {
    // Cap search input to 5 digits only
    const capped = String(val || "")
      .replace(/\D/g, "")
      .slice(0, 5);
    setSearchValue(capped);
  };

  const handleChange = (newValues) => {
    setSearchValue(""); // Clear search after adding a tag
    const normalized = normalizeZipTagValues(newValues);
    onChange?.(normalized);
  };

  const handleBlur = (e) => {
    setSearchValue(""); // Clear search on blur
    onBlur?.(e);
  };

  return (
    <Select
      {...rest}
      mode="tags"
      maxTagCount={999}
      placeholder="e.g. 97201"
      tokenSeparators={[",", " "]}
      open={false}
      maxCount={MAX_SERVICE_ZIPS}
      style={{ width: "100%" }}
      value={value}
      onChange={handleChange}
      onSearch={handleSearch}
      onBlur={handleBlur}
      searchValue={searchValue}
    />
  );
}

const VendorApplyPage = () => {
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [submittedProgram, setSubmittedProgram] = useState(PROGRAM_NETWORK);

  const watchedValues = Form.useWatch([], form);
  const canSubmit = isVendorApplyFormReady(watchedValues);

  // Deep-link only: ?program=priority_placement pre-checks the box (do not sync URL on toggle).
  useEffect(() => {
    const p = (searchParams.get("program") || "").toLowerCase();
    if (p === PROGRAM_PRIORITY) {
      form.setFieldsValue({ applyForPriorityPlacement: true });
    }
  }, [form, searchParams]);

  useEffect(() => {
    if (!done) return;
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    });
  }, [done]);

  const onFinishFailed = (errorInfo) => {
    // Safely log error info without circular references
    try {
      console.log("Form validation failed:", {
        errorFields: errorInfo.errorFields?.map((field) => ({
          name: field.name,
          errors: field.errors,
        })),
        outOfDate: errorInfo.outOfDate,
        values: "object", // Don't log values to avoid circular references
      });
    } catch (logError) {
      console.log("Form validation failed but could not log details");
    }

    // Find the first field with an error and scroll to it
    const firstErrorField = errorInfo.errorFields?.[0];
    if (firstErrorField) {
      const fieldName = firstErrorField.name?.[0];
      const fieldElement =
        document.querySelector(`[data-field="${fieldName}"]`) ||
        document.querySelector(`[id="${fieldName}"]`) ||
        document.querySelector(`input[name="${fieldName}"]`) ||
        document.querySelector(`textarea[name="${fieldName}"]`) ||
        document.querySelector(`[aria-label*="${fieldName}"]`);

      if (fieldElement) {
        fieldElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
        // Focus the field after scrolling
        setTimeout(() => {
          fieldElement.focus();
        }, 300);
      } else {
        // Fallback: scroll to top of form
        const formElement = document.querySelector(".ant-form");
        if (formElement) {
          formElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }
    }

    // Show user-friendly error message
    message.error("Please fill in all required fields before submitting.");
  };

  const onFinish = async (values) => {
    const priority = values.applyForPriorityPlacement === true;
    const zips = serviceZipsFromFormValue(values.zipCode);
    const zipCode = zips.join(", ");
    const phoneDigits = normalizeUsPhoneDigits(values.phone);
    const preferredInterest = Boolean(values.clumsePreferredVendorInterest);
    setSubmitting(true);
    try {
      const basePayload = {
        name: values.name,
        companyName: String(values.companyName || "").trim(),
        trade: Array.isArray(values.trade)
          ? values.trade.join(", ")
          : values.trade,
        trades: Array.isArray(values.trade)
          ? values.trade
          : String(values.trade || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean),
        licenseNumber: values.licenseNumber || "",
        zipCode,
        phone: phoneDigits,
        email: values.email,
        clumsePreferredVendorInterest: preferredInterest,
        additionalNotes: String(values.additionalNotes || "").trim(),
      };
      if (priority) {
        setSubmittedProgram(PROGRAM_PRIORITY);
        await submitVendorApply({
          ...basePayload,
          program: PROGRAM_PRIORITY,
        });
      } else {
        setSubmittedProgram(PROGRAM_NETWORK);
        await submitVendorApply({
          ...basePayload,
          program: PROGRAM_NETWORK,
        });
      }
      message.success("Application received.");
      setDone(true);
    } catch (e) {
      console.error("Vendor application submission error:", e);
      // Avoid circular reference issues when logging error
      const errorMessage =
        e?.message || e?.toString?.() || "Could not submit. Please try again.";
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <Helmet>
        <title>Trades & vendors | {APP_NAME}</title>
        <meta
          name="description"
          content="Join the Clumse vendor network for scoped repair and remodel bids from property owners. Know the job before you quote."
        />
      </Helmet>
      <Shell>
        <Card>
          {done ? (
            <SuccessPanel>
              <CheckCircleOutlined
                style={{
                  fontSize: 48,
                  color: ORANGE,
                  marginBottom: "0.85rem",
                }}
              />
              <SuccessTitle>Thank you</SuccessTitle>
              <Subhead style={{ marginBottom: 0 }}>
                {submittedProgram === PROGRAM_PRIORITY ? (
                  <>
                    We&apos;ve received your application. Watch your business
                    email.                     We&apos;ll send vetting details and next steps for{" "}
                    <VettedPriorityLink /> (
                    {VENDOR_PRIORITY_PLACEMENT_MONTHLY_DISPLAY} after approval).
                    Nothing is due until you complete checkout.
                  </>
                ) : (
                  <>
                    We&apos;ve received your details. Our team will reach out
                    shortly.
                  </>
                )}
              </Subhead>
            </SuccessPanel>
          ) : (
            <>
              <Headline>
                Apply to work
                <HeadlineSecondLine>with Clumse owners</HeadlineSecondLine>
              </Headline>
              <Subhead>
                Join the free <strong>Clumse vendor network</strong> to receive
                repair and remodel opportunities matched to your trades and
                service area. Optional <VettedPriorityLink /> on bid lists is{" "}
                {VENDOR_PRIORITY_PLACEMENT_MONTHLY_DISPLAY} after approval.
                Check the box below for vetting details. Nothing is billed on
                this page.
              </Subhead>
              <ValueList aria-label="Why join the Clumse vendor network">
                {VENDOR_VALUE_PROPS.map(({ Icon, title, text }) => (
                  <ValueItem key={title}>
                    <ValueIcon aria-hidden>
                      <Icon />
                    </ValueIcon>
                    <ValueCopy>
                      <strong>{title}.</strong> {text}
                    </ValueCopy>
                  </ValueItem>
                ))}
              </ValueList>

              <StyledForm
                form={form}
                layout="vertical"
                requiredMark
                initialValues={{
                  clumsePreferredVendorInterest: false,
                  applyForPriorityPlacement: false,
                  zipCode: [],
                  trade: [],
                  companyName: "",
                  additionalNotes: "",
                }}
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
              >
                <Form.Item
                  name="name"
                  label="Your name"
                  data-field="name"
                  rules={[
                    { required: true, message: "Please enter your name" },
                    { min: 2, message: "Name is too short" },
                  ]}
                >
                  <Input placeholder="Contact name" autoComplete="name" />
                </Form.Item>
                <Form.Item
                  name="companyName"
                  label="Business name (optional)"
                  data-field="companyName"
                  rules={[{ max: 160, message: "Too long" }]}
                >
                  <Input
                    placeholder="Company or DBA name"
                    autoComplete="organization"
                  />
                </Form.Item>
                <Form.Item
                  name="trade"
                  label="Trade / specialty"
                  className="vendor-trade-select"
                  data-field="trade"
                  rules={[
                    {
                      required: true,
                      message: "Please select at least one trade",
                    },
                  ]}
                >
                  <Select
                    mode="multiple"
                    maxTagCount={999}
                    placeholder="Select your trades and specialties"
                    options={VENDOR_TRADE_CATEGORIES.map((trade) => ({
                      label: trade,
                      value: trade,
                    }))}
                    filterOption={(inputValue, option) =>
                      option.label
                        .toLowerCase()
                        .includes(inputValue.toLowerCase())
                    }
                  />
                </Form.Item>
                <Form.Item
                  name="licenseNumber"
                  label="License number (optional)"
                  rules={[{ max: 80, message: "Too long" }]}
                >
                  <Input placeholder="State license or certification # (if applicable)" />
                </Form.Item>
                <Form.Item
                  className="vendor-service-zip-item"
                  name="zipCode"
                  label="Service ZIP codes"
                  data-field="zipCode"
                  normalize={normalizeZipTagValues}
                  extra={
                    <ServiceZipFormExtra>
                      <span>
                        Add every metro or county ZIP you cover. Type a 5-digit
                        ZIP, then Enter or comma.
                      </span>{" "}
                    </ServiceZipFormExtra>
                  }
                  rules={[
                    {
                      required: true,
                      message: "Add at least one 5-digit U.S. ZIP code",
                      validator: (_, value) => {
                        const zips = serviceZipsFromFormValue(value);
                        if (zips.length === 0) {
                          return Promise.reject(
                            new Error("Add at least one 5-digit U.S. ZIP code")
                          );
                        }
                        if (zips.length > MAX_SERVICE_ZIPS) {
                          return Promise.reject(
                            new Error(`At most ${MAX_SERVICE_ZIPS} ZIP codes`)
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <VendorServiceZipSelect />
                </Form.Item>
                <Form.Item
                  name="phone"
                  label="Cell Phone Number (For SMS Job Alerts)"
                  data-field="phone"
                  normalize={(value) => formatUsPhoneInput(value)}
                  rules={[
                    {
                      required: true,
                      message: "Cell phone number is required",
                    },
                    {
                      validator: (_, value) => {
                        if (!normalizeUsPhoneDigits(value)) {
                          return Promise.reject(
                            new Error(
                              "Enter a valid 10-digit U.S. phone number"
                            )
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder="e.g. (314) 555-0199"
                    autoComplete="tel"
                    inputMode="tel"
                    maxLength={14}
                  />
                </Form.Item>
                <Form.Item
                  name="email"
                  label="Business email"
                  data-field="email"
                  rules={[
                    { required: true, message: "Email is required" },
                    { type: "email", message: "Enter a valid email" },
                  ]}
                >
                  <Input
                    placeholder="you@yourcompany.com"
                    autoComplete="email"
                  />
                </Form.Item>

                <Form.Item
                  className="vendor-apply-notes-item"
                  name="additionalNotes"
                  label="Additional notes (optional)"
                  rules={[
                    {
                      max: 4000,
                      message: "Please keep notes under 4,000 characters",
                    },
                  ]}
                >
                  <Input.TextArea
                    placeholder="Insurance, crew size, years in business, or how you heard about Clumse."
                    rows={4}
                    showCount
                    maxLength={4000}
                  />
                </Form.Item>

                <Form.Item
                  name="applyForPriorityPlacement"
                  valuePropName="checked"
                  className="vendor-priority-placement-item"
                  colon={false}
                  label={
                    <PriorityPlacementHeader>
                      <PriorityPlacementEyebrow>
                        Premium placement
                      </PriorityPlacementEyebrow>
                      <OptionalPill>Optional</OptionalPill>
                    </PriorityPlacementHeader>
                  }
                >
                  <Checkbox className="vendor-apply-checkbox-compact">
                    I&apos;m interested in{" "}
                    <VettedPriorityLink stopPropagation /> on bid lists (
                    {VENDOR_PRIORITY_PLACEMENT_MONTHLY_DISPLAY} after
                    approval). We&apos;ll send vetting details to your email. No
                    charge until approved.
                  </Checkbox>
                </Form.Item>

                <Form.Item
                  name="clumsePreferredVendorInterest"
                  className="vendor-network-consent-item"
                  label="Vendor network & SMS consent"
                  valuePropName="checked"
                  required
                  rules={[
                    {
                      validator: (_, checked) =>
                        checked
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(
                                "Please confirm vendor network membership and SMS job alerts to continue"
                              )
                            ),
                    },
                  ]}
                >
                  <Checkbox className="vendor-apply-checkbox-compact">
                    I agree to join the free{" "}
                    <strong>Clumse vendor network</strong> and receive scoped
                    job invitations by email or SMS at the contact info I
                    provided. This is separate from optional{" "}
                    <VettedPriorityLink stopPropagation /> above. Msg &amp;
                    data rates may apply. Reply STOP to unsubscribe.
                  </Checkbox>
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                  <SubmitButton
                    type="primary"
                    htmlType="submit"
                    block
                    loading={submitting}
                    disabled={!canSubmit || submitting}
                  >
                    Submit application
                  </SubmitButton>
                </Form.Item>
              </StyledForm>
            </>
          )}
        </Card>
        <FooterNote>
          Property owner or exploring the product?{" "}
          <Link to="/">Back to {APP_NAME}</Link>
        </FooterNote>
      </Shell>
    </Page>
  );
};

export default VendorApplyPage;
